import { useEffect, useState } from 'react';
import CountryFilter from './CountryFilter';
import { locationApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useStoreStore } from '@/stores/store-store';
import { useDashboardStore } from '../../stores/dashboard-store';
import { useFilterStore } from '../../stores/filter-store';
import { useOperationFilterStore } from '../../stores/operation-filter-store';
import SearchBar from './SearchBar';
import FilterModal from './FilterModal';
import { Search } from 'lucide-react';
import NotificationDrawer from '../../pages/notifications/components/NotificationDrawer';
import HelpDeskDrawer from '../../pages/help-desk/components/HelpDeskDrawer';
import LanguageDropdown from '../language/LanguageDropdown';
import { useLocation, useSearchParams } from 'react-router-dom';

interface FilterToolbarProps {
  isDashboard?: boolean;
  showTakeInButton?: boolean;
  isTakeInActive?: boolean;
  onTakeInClick?: () => void;
  onSubmitNowClick?: () => void;
  showShipButton?: boolean;
  isShipActive?: boolean;
  onShipClick?: () => void;
  onShipNowClick?: () => void;
  showOutboundSubmit?: boolean;
  onOutboundSubmitClick?: () => void;
  showShipmentSubmit?: boolean;
  onShipmentSubmitClick?: () => void;
  // Destination Warehouse → In-Warehouse
  showToCenterButton?: boolean;
  isToCenterActive?: boolean;
  onToCenterClick?: () => void;
  showToCenterSubmit?: boolean;
  onToCenterSubmitClick?: () => void;
}

function FilterToolbar({ 
  isDashboard = false, 
  showTakeInButton = false, 
  isTakeInActive = false,
  onTakeInClick,
  onSubmitNowClick,
  showShipButton = false,
  isShipActive = false,
  onShipClick,
  onShipNowClick,
  showOutboundSubmit = false,
  onOutboundSubmitClick,
  showShipmentSubmit = false,
  onShipmentSubmitClick,
  showToCenterButton = false,
  isToCenterActive = false,
  onToCenterClick,
  showToCenterSubmit = false,
  onToCenterSubmitClick
}: FilterToolbarProps) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { filters, updateFilters } = useDashboardStore();
  const { 
    fulfillmentFilters, 
    toggleFulfillmentMarketplace,
    setFulfillmentSearchQuery,
    clearFulfillmentFilters
  } = useFilterStore();
  const {
    marketplaceFilter: operationMarketplaceFilter,
    filters: operationFilters,
    toggleMarketplace: toggleOperationMarketplace,
    setSearchQuery: setOperationSearchQuery,
    setFilters: setOperationFilters,
    clearAllFilters: clearOperationFilters
  } = useOperationFilterStore();
  
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isHelpDeskDrawerOpen, setIsHelpDeskDrawerOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [ordersSearchQuery, setOrdersSearchQuery] = useState('');
  const [isOrdersFilterModalOpen, setIsOrdersFilterModalOpen] = useState(false);

  // Determine if we're on fulfillment-like pages or operation page
  const isFulfillmentOnlyPage = pathname === '/fulfillment';
  const isOrdersPage = pathname === '/orders';
  const isFulfillmentPage = isFulfillmentOnlyPage || isOrdersPage; // Both use fulfillment filters
  const isOperationPage = pathname === '/operation';
  const isReportPage = pathname === '/report';
  const isSettingsPage = pathname === '/settings';
  const { token } = useAuthStore();
  const { currentStore } = useStoreStore();
  const [cities, setCities] = useState<Array<{ id: number; name: string }>>([]);
  const [areas, setAreas] = useState<Array<{ id: string; name: string; zone: string | null; city: string; distribution_center_id?: number }>>([]);
  const tab = searchParams.get('tab');
  const subStatus = searchParams.get('subStatus');
  const isDestinationWarehouseLocal =
    isOperationPage && tab === 'destination_warehouse' && (subStatus === 'in_warehouse' || subStatus === 'outbound');
  const isDistributionCenterLocal =
    isOperationPage && tab === 'distribution_center' && ['inbound', 'in_center', 'return', 'missing'].includes(subStatus || '');
  const usesCityAreaToolbar = isDestinationWarehouseLocal || isDistributionCenterLocal;

  const handleCountrySelect = (countryCode: string) => {
    if (isDashboard) {
      // Dashboard logic
      const currentFilter = filters.marketplaceFilter;
      const marketplaceCode = `${countryCode.toLowerCase()}_mkt`;
      const newFilter = currentFilter.includes(marketplaceCode)
        ? currentFilter.filter(c => c !== marketplaceCode)
        : [...currentFilter, marketplaceCode];
      
      updateFilters({ marketplaceFilter: newFilter });
    } else if (isFulfillmentPage) {
      // Fulfillment logic
      toggleFulfillmentMarketplace(countryCode);
    } else if (isOperationPage) {
      // On operation page, we keep country pills for most tabs, but for Destination Warehouse → In-Warehouse we replace with City/Area
      const marketplaceCode = `${countryCode.toLowerCase()}_mkt`;
      toggleOperationMarketplace(marketplaceCode);
    }
  };

  // Get selected countries based on context
  const getSelectedCountries = () => {
    if (isDashboard) {
      // Convert 'uk_mkt' format to 'UK' format for display
      return filters.marketplaceFilter.map(marketplace => 
        marketplace.replace('_mkt', '').toUpperCase()
      );
    } else if (isFulfillmentPage) {
      // Fulfillment also uses 'uk_mkt' format, convert to 'UK' for display
      return fulfillmentFilters.marketplaceFilter.map(marketplace => 
        marketplace.replace('_mkt', '').toUpperCase()
      );
    } else if (isOperationPage) {
      // Operation page uses operation filter store
      return operationMarketplaceFilter.map(marketplace => 
        marketplace.replace('_mkt', '').toUpperCase()
      );
    }
    return [];
  };

  const handleSearch = (query: string) => {
    if (isFulfillmentPage) {
      setFulfillmentSearchQuery(query);
    } else if (isOperationPage) {
      // For operation page, use operation filter store
      setOperationSearchQuery(query);
    }
  };

  const handleApplyFilters = (filters: any) => {
    if (isFulfillmentPage) {
      // The filters are already applied to the store by the FilterModal
      // The useFulfillment hook will automatically detect the changes and refetch
      console.log('Applied filters from modal:', filters);
    } else if (isOperationPage) {
      // For operation page, use operation filter store
      setOperationFilters(filters);
    }
  };

  const handleClearFilters = () => {
    if (isFulfillmentPage) {
      clearFulfillmentFilters();
      setFulfillmentSearchQuery('');
    } else if (isOperationPage) {
      // For operation page, use operation filter store
      clearOperationFilters();
    }
  };

  const openOrdersFilterModal = () => setIsOrdersFilterModalOpen(true);
  const closeOrdersFilterModal = () => setIsOrdersFilterModalOpen(false);

  const openNotificationDrawer = () => setIsNotificationDrawerOpen(true);
  const closeNotificationDrawer = () => setIsNotificationDrawerOpen(false);

  const openHelpDeskDrawer = () => setIsHelpDeskDrawerOpen(true);
  const closeHelpDeskDrawer = () => setIsHelpDeskDrawerOpen(false);

  const toggleLanguageDropdown = () => setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  const closeLanguageDropdown = () => setIsLanguageDropdownOpen(false);

  // Load cities whenever operation tab/subtab context changes to destination warehouse local flows.
  useEffect(() => {
    let cancelled = false;

    if (usesCityAreaToolbar && token && currentStore?.id) {
      locationApi
        .getCities(token, currentStore.id)
        .then((res) => {
          if (!cancelled) {
            const nextCities = res.success ? (res.data || []) : [];
            setCities(nextCities);

            if (isDistributionCenterLocal && operationFilters?.city && !operationFilters?.distributionCenterId) {
              const selectedCenter = nextCities.find((center) => center.name === operationFilters.city);
              if (selectedCenter) {
                setOperationFilters({ distributionCenterId: selectedCenter.id });
              }
            }
          }
        })
        .catch(() => {
          if (!cancelled) setCities([]);
        });
    } else {
      setCities([]);
      setAreas([]);
    }

    return () => {
      cancelled = true;
    };
  }, [usesCityAreaToolbar, token, currentStore?.id, isDistributionCenterLocal, operationFilters?.city, operationFilters?.distributionCenterId, setOperationFilters]);

  // Re-load areas for persisted city selection when revisiting tab/subtab.
  useEffect(() => {
    let cancelled = false;
    const selectedCity = operationFilters?.city;

    if (usesCityAreaToolbar && selectedCity && token && currentStore?.id) {
      locationApi
        .getAreas(token, currentStore.id, selectedCity)
        .then((res) => {
          if (!cancelled) {
            setAreas(res.success ? ((res.data as any) || []) : []);
          }
        })
        .catch(() => {
          if (!cancelled) setAreas([]);
        });
    } else if (!selectedCity) {
      setAreas([]);
    }

    return () => {
      cancelled = true;
    };
  }, [usesCityAreaToolbar, operationFilters?.city, token, currentStore?.id]);

  useEffect(() => {
    if (isOrdersPage) {
      setOrdersSearchQuery(fulfillmentFilters.searchQuery || '');
    }
  }, [isOrdersPage, fulfillmentFilters.searchQuery]);

  const handleCityChange = async (value: string) => {
    const selectedCity = isDistributionCenterLocal
      ? cities.find((city) => String(city.id) === value)
      : cities.find((city) => city.name === value);
    const cityName = selectedCity?.name || value;

    setOperationFilters({
      city: cityName,
      area: '',
      distributionCenterId: isDistributionCenterLocal ? selectedCity?.id : undefined,
    });
    setAreas([]);
    if (cityName && token && currentStore?.id) {
      const res = await locationApi.getAreas(token, currentStore.id, cityName);
      if (res.success) setAreas((res.data as any) || []);
    }
  };

  const handleAreaChange = (value: string) => {
    // Find selected area to get distribution_center_id
    const selected = areas.find(a => a.name === value);
    setOperationFilters({ area: value, distributionCenterId: selected?.distribution_center_id });
  };

  // Dashboard-specific design
  if (isDashboard) {
    return (
      <>
        <div className="mx-16 rounded-full border border-[#ECEEF3] bg-white px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Country Filter - Left Side */}
            <CountryFilter 
              onSelect={handleCountrySelect}
              selected={getSelectedCountries()}
              context="dashboard"
            />

            {/* Icons - Right Side */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div 
                  className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                  onClick={toggleLanguageDropdown}
                >
                  <img src="/img/globe.svg" className="h-[19px] w-[19px]" alt="Language" />
                </div>
                <LanguageDropdown 
                  isOpen={isLanguageDropdownOpen} 
                  onClose={closeLanguageDropdown} 
                />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openNotificationDrawer}
              >
                <img src="/img/Union.svg" className="h-[19px] w-[20px]" alt="Notifications" />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openHelpDeskDrawer}
              >
                <img src="/img/message-notify.svg" className="h-[19px] w-[19px]" alt="Help Desk" />
              </div>
            </div>
          </div>
        </div>

        {/* Notification Drawer */}
        <NotificationDrawer 
          isOpen={isNotificationDrawerOpen} 
          onClose={closeNotificationDrawer} 
        />

        {/* Help Desk Drawer */}
        <HelpDeskDrawer 
          isOpen={isHelpDeskDrawerOpen} 
          onClose={closeHelpDeskDrawer} 
        />
      </>
    );
  }

  // Default design for other pages   // For Orders page: Two separate toolbars
  if (isOrdersPage) {
    return (
      <>
        {/* First Toolbar: Country Filter + Icons (Globe, Notification, Chat) */}
        <div className="bg-white rounded-full outline outline-1 outline-gray-200 px-6 py-4 mx-16 mb-4">
          <div className="flex items-center justify-between gap-4">
            {/* Country Filter - Left */}
            <CountryFilter 
              selected={getSelectedCountries()}
              context="fulfillment"
              onSelect={undefined}
            />
            
            {/* Icons - Right */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div 
                  className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                  onClick={toggleLanguageDropdown}
                >
                  <img src="/img/globe.svg" className="h-[19px] w-[19px]" alt="Language" />
                </div>
                <LanguageDropdown 
                  isOpen={isLanguageDropdownOpen} 
                  onClose={closeLanguageDropdown} 
                />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openNotificationDrawer}
              >
                <img src="/img/Union.svg" className="h-[19px] w-[20px]" alt="Notifications" />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openHelpDeskDrawer}
              >
                <img src="/img/message-notify.svg" className="h-[19px] w-[19px]" alt="Help Desk" />
              </div>
            </div>
          </div>
        </div>

        {/* Second Toolbar: Search (Left) + Filter + Refresh (Right) */}
        <div className="bg-white rounded-full outline outline-1 outline-gray-200 px-6 py-4 mx-16">
          <div className="flex items-center justify-between gap-4">
            {/* Search Input - Left Side */}
            <form
              className="relative w-56"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch(ordersSearchQuery);
              }}
            >
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search for Orders"
                value={ordersSearchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setOrdersSearchQuery(value);
                  setFulfillmentSearchQuery(value);
                }}
                className="pl-10 pr-4 py-2.5 rounded-full border border-gray-300 bg-gray-100 font-medium text-gray-900 placeholder-gray-500 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:border-brand-secondary"
              />
            </form>

            {/* Filter + Refresh Buttons - Right Side */}
            <div className="flex gap-3 items-center">
              <button
                type="button"
                onClick={openOrdersFilterModal}
                className="px-4 py-2.5 rounded-lg bg-[#FFF9E0] text-gray-700 font-medium text-sm flex items-center gap-2 hover:bg-yellow-200 transition"
              >
                <img
                  src="/img/filter.svg"
                  alt="Filter"
                  className="w-5 h-5"
                />
                Filter
              </button>
              <button
                type="button"
                onClick={() => {
                  setOrdersSearchQuery('');
                  handleClearFilters();
                }}
                className="p-2.5 rounded-md bg-gray-200 text-gray-600 hover:bg-gray-300 transition"
                title="Clear all filters and search"
              >
                <img
                  src="/img/refresh.svg"
                  alt="Refresh"
                  className="w-4 h-4"
                />
              </button>
            </div>
          </div>
        </div>

        <FilterModal
          isOpen={isOrdersFilterModalOpen}
          onClose={closeOrdersFilterModal}
          context="fulfillment"
          onApplyFilters={handleApplyFilters}
        />

        {/* Notification Drawer */}
        <NotificationDrawer 
          isOpen={isNotificationDrawerOpen} 
          onClose={closeNotificationDrawer} 
        />

        {/* Help Desk Drawer */}
        <HelpDeskDrawer 
          isOpen={isHelpDeskDrawerOpen} 
          onClose={closeHelpDeskDrawer} 
        />
      </>
    );
  }

  // For Fulfillment page: Single toolbar with country filter + search/filter/refresh (NO icons)
  if (isFulfillmentOnlyPage) {
    return (
      <>
        <div className="mx-16 rounded-full border border-[#E7EAF3] bg-white px-7 py-2.5">
          <div className="flex items-center justify-between gap-5">
            {/* Country Filter - Left */}
            <CountryFilter 
              selected={getSelectedCountries()}
              context="fulfillment"
              onSelect={undefined}
            />

            {/* Search Bar + Filter + Refresh - Right */}
            <SearchBar 
              onSearch={handleSearch}
              context="fulfillment"
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
          </div>
        </div>

        {/* Notification Drawer */}
        <NotificationDrawer 
          isOpen={isNotificationDrawerOpen} 
          onClose={closeNotificationDrawer} 
        />

        {/* Help Desk Drawer */}
        <HelpDeskDrawer 
          isOpen={isHelpDeskDrawerOpen} 
          onClose={closeHelpDeskDrawer} 
        />
      </>
    );
  }

  // Default design for other pages (Operation, Report, etc.)
  const showSubmitNow = (showTakeInButton && isTakeInActive) || (showShipButton && isShipActive) || showOutboundSubmit || showShipmentSubmit || (showToCenterSubmit && isToCenterActive);

  return (
    <>
      {/* Hide toolbar (country filter + search) on Report and Settings as per Figma */}
      {!isReportPage && !isSettingsPage && (
      <div className={`bg-white rounded-full outline outline-1 outline-gray-200 mx-16 overflow-hidden ${showSubmitNow ? 'pl-6 pr-0 py-0' : 'px-6 py-4'}`}>
        <div className={`flex justify-between ${showSubmitNow ? 'items-stretch' : 'items-center'} gap-4 relative`}>
          <div className={`flex items-center gap-4 flex-1 min-w-0 ${showSubmitNow ? 'py-4' : ''}`}>
          {/* Country Filter or City/Area for Destination Warehouse → In-Warehouse */}
          {(() => {
            if (usesCityAreaToolbar) {
              return (
                <div className="flex items-center gap-3">
                  <select
                    className="w-56 md:w-64 border border-gray-200 rounded-full px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={isDistributionCenterLocal ? String(operationFilters?.distributionCenterId || '') : (operationFilters?.city || '')}
                    onChange={(e) => handleCityChange(e.target.value)}
                  >
                    <option value="">Select City</option>
                    {cities.map((c) => (
                      <option key={c.id} value={isDistributionCenterLocal ? String(c.id) : c.name}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    className="w-56 md:w-64 border border-gray-200 rounded-full px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={operationFilters?.area || ''}
                    onChange={(e) => handleAreaChange(e.target.value)}
                    disabled={!operationFilters?.city}
                  >
                    <option value="">Select Area</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.name}>{a.name}</option>
                    ))}
                  </select>
                  {operationFilters?.city && onToCenterClick && showToCenterButton && subStatus === 'in_warehouse' && (
                    <button
                      onClick={onToCenterClick}
                      className={`px-5 py-2 rounded-full text-xs md:text-sm font-medium transition-colors ${
                        isToCenterActive ? 'bg-black text-white hover:bg-gray-800' : 'bg-yellow-400 hover:bg-yellow-500 text-black'
                      }`}
                    >
                      {isToCenterActive ? 'Active' : 'To Center'}
                    </button>
                  )}
                </div>
              );
            }
            return (
              <CountryFilter 
                selected={getSelectedCountries()}
                context={isFulfillmentPage ? "fulfillment" : (isOperationPage ? "operation" : "dashboard")}
                onSelect={isFulfillmentPage ? undefined : (isOperationPage ? undefined : handleCountrySelect)}
              />
            );
          })()}
          </div>

          {/* Take-in / Ship Button — absolutely centered in the row */}
          {(showTakeInButton || showShipButton) && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center">
              {/* Take-in Button */}
              {showTakeInButton && onTakeInClick && (
                <button
                  onClick={onTakeInClick}
                  className={`flex h-9 items-center gap-1.5 rounded-[10px] px-3.5 text-[12px] font-semibold transition-colors ${
                    isTakeInActive 
                      ? 'bg-[#171922] text-white hover:bg-black' 
                      : 'bg-[#FFC107] text-[#1D2233] hover:bg-[#F0B400]'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] ${
                      isTakeInActive ? 'bg-white/16' : 'bg-[#1D2233]'
                    }`}
                  >
                    <svg
                      className={`h-[9px] w-[9px] ${isTakeInActive ? 'text-white' : 'text-[#FFC107]'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.75 3.5 7.1v2.2h17V7.1L12 2.75Zm-4.9 8.8v5.2H5.35v2.2h13.3v-2.2H16.9v-5.2H7.1Zm2.2 0h5.4v5.2H9.3v-5.2ZM2.25 20.95h19.5v-2.2H2.25v2.2Z" />
                    </svg>
                  </span>
                  <span className="whitespace-nowrap">{isTakeInActive ? 'Active' : 'Take-in'}</span>
                </button>
              )}

              {/* Ship Button */}
              {showShipButton && onShipClick && (
                <button
                  onClick={onShipClick}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 md:px-6 md:py-2 md:text-sm md:gap-2 ${
                    isShipActive 
                      ? 'bg-black hover:bg-gray-800 text-white' 
                      : 'bg-yellow-400 hover:bg-yellow-500 text-black'
                  }`}
                >
                  <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <span className="hidden sm:inline">{isShipActive ? 'Active' : 'Ship'}</span>
                  <span className="sm:hidden">{isShipActive ? 'Active' : 'Ship'}</span>
                </button>
              )}
            </div>
          )}

          {/* Search Bar - pushed to right end */}
          <div className="ml-auto flex items-center">
            <SearchBar 
              onSearch={handleSearch}
              context={isFulfillmentPage ? "fulfillment" : (isOperationPage ? "operation" : "dashboard")}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
          </div>

          {/* Icons - Right Side (for fulfillment/orders pages) */}
          {isFulfillmentPage && (
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div 
                  className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                  onClick={toggleLanguageDropdown}
                >
                  <img src="/img/globe.svg" className="h-[19px] w-[19px]" alt="Language" />
                </div>
                <LanguageDropdown 
                  isOpen={isLanguageDropdownOpen} 
                  onClose={closeLanguageDropdown} 
                />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openNotificationDrawer}
              >
                <img src="/img/Union.svg" className="h-[19px] w-[20px]" alt="Notifications" />
              </div>
              <div 
                className="h-[44px] w-[44px] bg-[#EEF0F4] rounded-xl flex items-center justify-center hover:bg-[#E7EAF0] transition-colors cursor-pointer"
                onClick={openHelpDeskDrawer}
              >
                <img src="/img/message-notify.svg" className="h-[19px] w-[19px]" alt="Help Desk" />
              </div>
            </div>
          )}

          {/* Submit Now */}
          {showSubmitNow && (
            <button 
              onClick={
                isShipActive
                  ? onShipNowClick
                  : showOutboundSubmit
                    ? onOutboundSubmitClick
                    : showShipmentSubmit
                      ? onShipmentSubmitClick
                      : showToCenterSubmit && isToCenterActive && onToCenterSubmitClick
                        ? onToCenterSubmitClick
                        : onSubmitNowClick
              }
              className="bg-[#111827] hover:bg-[#1f2937] active:bg-[#0b0f19] text-white px-8 rounded-r-full text-[15px] font-semibold whitespace-nowrap transition-all duration-150 ease-in-out flex items-center"
            >
              Submit Now
            </button>
          )}
        </div>
      </div>
      )}

      {/* Notification Drawer */}
      <NotificationDrawer 
        isOpen={isNotificationDrawerOpen} 
        onClose={closeNotificationDrawer} 
      />

      {/* Help Desk Drawer */}
      <HelpDeskDrawer 
        isOpen={isHelpDeskDrawerOpen} 
        onClose={closeHelpDeskDrawer} 
      />
    </>
  );
}

export default FilterToolbar; 